import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslate } from "ra-core";
import { useCallback, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import {
  hayCambiosSinGuardar,
  olvidarCambiosSinGuardar,
} from "./cambiosSinGuardar";
import { FichaNegocio } from "./FichaNegocio";
import {
  PARAMETRO_FICHA as PARAMETRO,
  PARAMETRO_PROYECTO,
  type EstadoVentana,
} from "./useAbrirFicha";

/**
 * La ficha se abre siempre en una ventana encima de la pantalla en la que se está
 * (petición de Robert, 02/10/2026: «una sola forma de hacer cada cosa»): tablero, Leads,
 * agenda, inicio y buscador. La ventana vive en la URL (`?ficha=123`): el botón «atrás»
 * del móvil la cierra, recargar la mantiene y el enlace se puede compartir. Se monta una
 * vez en cada layout (escritorio y móvil).
 */
export const VentanaFicha = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const parametros = new URLSearchParams(location.search);
  const id = parametros.get(PARAMETRO);
  const proyecto = parametros.get(PARAMETRO_PROYECTO);

  const cerrar = useCallback(() => {
    // Si la abrimos nosotros, «atrás» deja el historial como estaba; si se llegó con el
    // enlace directo, se quita el parámetro sin añadir otra entrada.
    if ((location.state as EstadoVentana)?.ventanaFicha) {
      navigate(-1);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete(PARAMETRO);
    params.delete(PARAMETRO_PROYECTO);
    const resto = params.toString();
    navigate(
      { pathname: location.pathname, search: resto ? `?${resto}` : "" },
      { replace: true },
    );
  }, [location.pathname, location.search, location.state, navigate]);

  // Auditoría 30/10/2026: con cambios sin guardar, Esc, el clic fuera y la X preguntan antes.
  const [confirmar, setConfirmar] = useState(false);
  const intentarCerrar = useCallback(() => {
    if (hayCambiosSinGuardar()) setConfirmar(true);
    else cerrar();
  }, [cerrar]);

  return (
    <>
      <DialogPrimitive.Root
        open={!!id}
        onOpenChange={(abierta) => !abierta && intentarCerrar()}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className={cn(
              "fixed z-50 bg-background shadow-elevada overflow-y-auto focus:outline-none",
              isMobile
                ? "inset-0"
                : "left-1/2 top-[4vh] h-[92vh] w-[min(96vw,80rem)] -translate-x-1/2 rounded-2xl border border-border/60",
            )}
          >
            <DialogPrimitive.Title className="sr-only">
              {translate("nevora.negocios.ficha")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="absolute right-3 top-3 z-10 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={translate("nevora.negocios.cerrar_ficha")}
            >
              <X className="size-5" aria-hidden />
            </DialogPrimitive.Close>
            <div
              className={cn(isMobile ? "px-4 pt-14 pb-6" : "px-8 pt-8 pb-6")}
            >
              {id ? (
                <FichaNegocio
                  key={`${id}-${proyecto ?? ""}`}
                  id={id}
                  proyectoId={proyecto}
                  enVentana
                  onCerrar={intentarCerrar}
                />
              ) : null}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <Dialog open={confirmar} onOpenChange={setConfirmar}>
        <DialogContent className="sm:max-w-md" data-testid="sin-guardar">
          <DialogHeader>
            <DialogTitle>
              {translate("nevora.negocios.sin_guardar_titulo")}
            </DialogTitle>
            <DialogDescription>
              {translate("nevora.negocios.sin_guardar_texto")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmar(false)}>
              {translate("nevora.negocios.seguir_editando")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                olvidarCambiosSinGuardar();
                setConfirmar(false);
                cerrar();
              }}
            >
              {translate("nevora.negocios.cerrar_sin_guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Las rutas antiguas `/negocios/:id` y `/negocios/:id/show` abren la ventana sobre Leads:
 * los enlaces guardados siguen funcionando y solo hay una forma de ver una ficha.
 */
export const RedirigirAVentana = () => {
  const { id } = useParams();
  return <Navigate to={`/negocios?${PARAMETRO}=${id}`} replace />;
};
